import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Image,
  ActivityIndicator,
  Alert,
  StyleSheet,
} from "react-native";
import React, { useState, useMemo } from "react";
import * as ImagePicker from "expo-image-picker";
import { doc, setDoc, writeBatch, getDocs, collection as fsCollection } from "firebase/firestore";
import { db, storage } from "../firebase";
import { useStateContext } from "../lib/context";
import Header from "../components/Header";
import { Ionicons } from "@expo/vector-icons";
import Images from "../assets/img";
import AsyncStorage from "@react-native-async-storage/async-storage";

const AdminImageUpload = ({ navigation }) => {
  const { lastProd, productImages, setProductImages } = useStateContext();

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [pickedImage, setPickedImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateProgress, setMigrateProgress] = useState("");
  const [migrateConfirm, setMigrateConfirm] = useState(false);
  const [migrateLog, setMigrateLog] = useState([]);

  // Filtered product suggestions (min 2 chars)
  const suggestions = useMemo(() => {
    if (search.length < 2) return [];
    const q = search.toUpperCase();
    return lastProd
      .filter(
        (p) =>
          p.DENOMINACION.toUpperCase().includes(q) ||
          p.ARTICULO__ID.toUpperCase().includes(q)
      )
      .slice(0, 8);
  }, [search, lastProd]);

  const selectProduct = (product) => {
    setSelected(product);
    setSearch(product.DENOMINACION);
    setPickedImage(null);
  };

  const pickImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      Alert.alert("Permiso requerido", "Se necesita acceso a la galería para subir imágenes.");
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.8,
    });
    if (!result.canceled && result.assets?.[0]) {
      setPickedImage(result.assets[0].uri);
    }
  };

  const uploadImage = async () => {
    if (!selected || !pickedImage) return;
    setUploading(true);
    try {
      // Fetch the image as a blob
      const response = await fetch(pickedImage);
      const blob = await response.blob();

      // Derive extension safely — data URIs need special handling
      const sku = selected.ARTICULO__ID;
      let ext = "jpg";
      if (pickedImage.startsWith("data:")) {
        const mimeMatch = pickedImage.match(/data:image\/([^;]+)/);
        ext = mimeMatch ? mimeMatch[1] : "jpg";
      } else {
        ext = pickedImage.split(".").pop().split("?")[0] || "jpg";
      }
      console.log("[Upload] SKU:", sku, "| ext:", ext, "| blob size:", blob.size);
      const ref = storage.ref(`productImages/${sku}.${ext}`);
      await ref.put(blob);
      const downloadURL = await ref.getDownloadURL();
      console.log("[Upload] Download URL:", downloadURL);

      // Save URL to Firestore
      await setDoc(doc(db, "productImages", sku), { url: downloadURL });

      // Update local context map and AsyncStorage cache
      const updatedMap = new Map(productImages).set(sku, downloadURL);
      setProductImages(updatedMap);
      const period = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}-${d.getHours() < 12 ? "AM" : "PM"}`;
      })();
      await AsyncStorage.setItem("CACHE_PRODUCT_IMAGES", JSON.stringify([...updatedMap]));
      await AsyncStorage.setItem("CACHE_PRODUCT_IMAGES_PERIOD", period);

      Alert.alert("¡Listo!", `Imagen subida para ${selected.DENOMINACION}`);
      setPickedImage(null);
      setSelected(null);
      setSearch("");
    } catch (e) {
      Alert.alert("Error", "No se pudo subir la imagen: " + e.message);
    }
    setUploading(false);
  };

  const log = (msg) => {
    console.log("[Migration]", msg);
    setMigrateLog((prev) => [...prev, msg]);
  };

  // One-time migration: img.js → Firestore productImages
  const runMigration = async () => {
    setMigrating(true);
    setMigrateLog([]);
    try {
      log(`img.js tiene ${Images.length} entradas totales`);

      const validEntries = Images.filter((e) => e.SKU && e["Imágenes"]);
      log(`Entradas válidas con URL: ${validEntries.length}`);

      if (validEntries.length === 0) {
        log("ERROR: No se encontraron entradas válidas. Verificar formato de img.js");
        setMigrating(false);
        return;
      }

      const BATCH_SIZE = 400;
      let done = 0;

      for (let i = 0; i < validEntries.length; i += BATCH_SIZE) {
        const chunk = validEntries.slice(i, i + BATCH_SIZE);
        log(`Escribiendo lote ${Math.floor(i / BATCH_SIZE) + 1}: entradas ${i + 1}–${i + chunk.length}`);
        const batch = writeBatch(db);
        chunk.forEach((entry) => {
          batch.set(doc(db, "productImages", entry.SKU), { url: entry["Imágenes"] });
        });
        await batch.commit();
        done += chunk.length;
        setMigrateProgress(`${done} / ${validEntries.length}`);
        log(`Lote completado. Total hasta ahora: ${done}`);
      }

      log("Recargando mapa local desde Firestore...");
      const snap = await getDocs(fsCollection(db, "productImages"));
      const map = new Map();
      snap.forEach((d) => { if (d.data().url) map.set(d.id, d.data().url); });
      log(`Firestore confirmó: ${map.size} entradas leídas.`);

      // Update AsyncStorage cache so next app restart loads correctly
      const period = (() => {
        const d = new Date();
        return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}-${d.getHours() < 12 ? "AM" : "PM"}`;
      })();
      await AsyncStorage.setItem("CACHE_PRODUCT_IMAGES", JSON.stringify([...map]));
      await AsyncStorage.setItem("CACHE_PRODUCT_IMAGES_PERIOD", period);
      log("Cache de AsyncStorage actualizado.");

      setProductImages(map);
      log(`¡MIGRACIÓN COMPLETA! ${done} imágenes escritas. Mapa local: ${map.size} entradas.`);
    } catch (e) {
      log(`ERROR: ${e.message}`);
      console.error("[Migration] Full error:", e);
    }
    setMigrating(false);
  };

  const currentImage = selected
    ? productImages.get(selected.ARTICULO__ID) || null
    : null;

  return (
    <ScrollView style={s.container} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
      <Header navigation={navigation} title="Imágenes de Productos" />

      {/* Search */}
      <View style={s.card}>
        <Text style={s.label}>Buscar producto</Text>
        <View style={s.searchWrapper}>
          <Ionicons name="search" size={18} color="#999" style={{ marginRight: 8 }} />
          <TextInput
            style={s.searchInput}
            placeholder="Nombre o código..."
            placeholderTextColor="#aaa"
            value={search}
            onChangeText={(t) => { setSearch(t); setSelected(null); setPickedImage(null); }}
          />
          {search.length > 0 && (
            <TouchableOpacity onPress={() => { setSearch(""); setSelected(null); setPickedImage(null); }}>
              <Ionicons name="close-circle" size={18} color="#ccc" />
            </TouchableOpacity>
          )}
        </View>

        {suggestions.length > 0 && !selected && (
          <View style={s.suggestionsBox}>
            {suggestions.map((p) => (
              <TouchableOpacity
                key={p.ARTICULO__ID}
                style={s.suggestionItem}
                onPress={() => selectProduct(p)}
              >
                <Text style={s.suggestionSku}>{p.ARTICULO__ID}</Text>
                <Text style={s.suggestionName} numberOfLines={1}>{p.DENOMINACION}</Text>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </View>

      {/* Selected product */}
      {selected && (
        <View style={s.card}>
          <Text style={s.sectionTitle}>{selected.DENOMINACION}</Text>
          <Text style={s.skuText}>{selected.ARTICULO__ID}</Text>

          {/* Current image */}
          <Text style={s.label}>Imagen actual</Text>
          <Image
            source={currentImage ? { uri: currentImage } : require("../assets/placeholder.png")}
            style={s.previewImage}
            resizeMode="contain"
          />

          {/* New image picker */}
          <TouchableOpacity style={s.pickBtn} onPress={pickImage}>
            <Ionicons name="image-outline" size={20} color="#2a2a8e" style={{ marginRight: 8 }} />
            <Text style={s.pickBtnText}>
              {pickedImage ? "Cambiar imagen seleccionada" : "Seleccionar nueva imagen"}
            </Text>
          </TouchableOpacity>

          {pickedImage && (
            <>
              <Text style={s.label}>Nueva imagen</Text>
              <Image source={{ uri: pickedImage }} style={s.previewImage} resizeMode="contain" />
              <TouchableOpacity
                style={[s.uploadBtn, uploading && s.uploadBtnDisabled]}
                onPress={uploadImage}
                disabled={uploading}
              >
                {uploading ? (
                  <ActivityIndicator color="#fff" size="small" />
                ) : (
                  <>
                    <Ionicons name="cloud-upload-outline" size={20} color="#fff" style={{ marginRight: 8 }} />
                    <Text style={s.uploadBtnText}>Subir imagen</Text>
                  </>
                )}
              </TouchableOpacity>
            </>
          )}
        </View>
      )}

      {/* One-time migration */}
      <View style={s.card}>
        <Text style={s.sectionTitle}>Migración única</Text>
        <Text style={s.mutedText}>
          Copia todas las imágenes de img.js a Firestore "productImages". Solo es necesario hacerlo una vez.
        </Text>

        {!migrateConfirm && !migrating && migrateLog.length === 0 && (
          <TouchableOpacity style={s.migrateBtn} onPress={() => setMigrateConfirm(true)}>
            <Ionicons name="sync-outline" size={18} color="#2a2a8e" style={{ marginRight: 8 }} />
            <Text style={s.migrateBtnText}>Migrar img.js → Firestore</Text>
          </TouchableOpacity>
        )}

        {migrateConfirm && !migrating && (
          <View style={s.confirmBox}>
            <Text style={s.confirmText}>
              ¿Confirmar? Se escribirán {Images.length} entradas a Firestore.
            </Text>
            <View style={s.confirmRow}>
              <TouchableOpacity style={s.confirmCancel} onPress={() => setMigrateConfirm(false)}>
                <Text style={s.confirmCancelText}>Cancelar</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={s.uploadBtn}
                onPress={() => { setMigrateConfirm(false); runMigration(); }}
              >
                <Text style={s.uploadBtnText}>Confirmar</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {(migrating || migrateLog.length > 0) && (
          <View style={s.logBox}>
            {migrateProgress !== "" && migrating && (
              <View style={s.progressRow}>
                <ActivityIndicator color="#2a2a8e" size="small" />
                <Text style={s.progressText}>Progreso: {migrateProgress}</Text>
              </View>
            )}
            {migrateLog.map((line, i) => (
              <Text key={i} style={[s.logLine, line.startsWith("ERROR") && s.logError, line.startsWith("¡") && s.logSuccess]}>
                {line}
              </Text>
            ))}
          </View>
        )}
      </View>

      <View style={{ height: 60 }} />
    </ScrollView>
  );
};

export default AdminImageUpload;

const s = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#F4F6FD" },
  card: {
    margin: 16,
    marginBottom: 0,
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  label: { fontSize: 13, color: "#777", marginBottom: 6, marginTop: 12, fontWeight: "600" },
  sectionTitle: { fontSize: 16, fontWeight: "bold", color: "#2a2a8e", marginBottom: 4 },
  skuText: { fontSize: 12, color: "#999", marginBottom: 8, fontFamily: "monospace" },
  mutedText: { fontSize: 13, color: "#999", lineHeight: 18, marginBottom: 12 },
  searchWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F4F6FD",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#ddd",
    paddingHorizontal: 12,
    height: 44,
  },
  searchInput: { flex: 1, fontSize: 15, color: "#333" },
  suggestionsBox: {
    marginTop: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#eee",
    overflow: "hidden",
  },
  suggestionItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#f5f5f5",
    gap: 10,
  },
  suggestionSku: { fontSize: 11, color: "#999", fontFamily: "monospace", minWidth: 90 },
  suggestionName: { fontSize: 14, color: "#333", flex: 1 },
  previewImage: {
    width: "100%",
    height: 180,
    borderRadius: 8,
    backgroundColor: "#f5f5f5",
    marginBottom: 12,
  },
  pickBtn: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#2a2a8e",
    borderRadius: 8,
    padding: 12,
    justifyContent: "center",
    marginBottom: 8,
  },
  pickBtnText: { color: "#2a2a8e", fontWeight: "600", fontSize: 14 },
  uploadBtn: {
    flexDirection: "row",
    backgroundColor: "#2a2a8e",
    borderRadius: 8,
    padding: 14,
    justifyContent: "center",
    alignItems: "center",
  },
  uploadBtnDisabled: { opacity: 0.5 },
  uploadBtnText: { color: "#fff", fontWeight: "bold", fontSize: 15 },
  migrateBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#2a2a8e",
    borderRadius: 8,
    padding: 12,
  },
  migrateBtnText: { color: "#2a2a8e", fontWeight: "600", fontSize: 14 },
  progressRow: { flexDirection: "row", alignItems: "center", gap: 10, padding: 8 },
  progressText: { color: "#2a2a8e", fontSize: 14 },
  confirmBox: { backgroundColor: "#f0f4ff", borderRadius: 8, padding: 12, marginTop: 8 },
  confirmText: { fontSize: 14, color: "#333", marginBottom: 12 },
  confirmRow: { flexDirection: "row", gap: 10 },
  confirmCancel: {
    flex: 1, borderWidth: 1, borderColor: "#ccc", borderRadius: 8,
    padding: 10, alignItems: "center",
  },
  confirmCancelText: { color: "#666", fontWeight: "600" },
  logBox: {
    marginTop: 10, backgroundColor: "#1a1a2e", borderRadius: 8,
    padding: 12,
  },
  logLine: { color: "#ccc", fontSize: 12, fontFamily: "monospace", marginBottom: 3 },
  logError: { color: "#ff6b6b" },
  logSuccess: { color: "#51cf66", fontWeight: "bold" },
});
